import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { omit } from 'lodash';
import { EmptyState, Flex, Spinner } from 'core/components';
import { showErrorToast } from 'core/components/toast';
import Page from 'app/components/page/Page';
import NotFound from 'app/views/NotFound';
import { GRID_CONTAINER_PADDING, GRID_MARGIN, GRID_ROW_HEIGHT } from 'app/stores/boards/constants';
import { BOARD_LABEL } from '@kentik/ui-shared/boards/boards';
import { getHashForQuery } from 'app/stores/query/urlHash';
import BoardGridLayout from './BoardGridLayout';
import { ChartSyncProvider } from './useChartSync';
import { AddPanelButton } from './AddPanelButton';

@withRouter
@inject('$app', '$savedViews')
@observer
class BoardGridView extends Component {
  get basePath() {
    const { location } = this.props;
    return location.pathname.startsWith('/v4/ude') ? '/v4/ude' : '/v4/library';
  }

  handleRemovePanel = async (panel) => {
    const { board } = this.props;
    try {
      await board.removePanel(panel);
    } catch (error) {
      console.error('Failed to remove panel', error);
      showErrorToast('Failed to remove panel. Please try again.');
    }
  };

  handleDuplicatePanel = (panel) => {
    const { board } = this.props;
    const attrs = panel.serialize(panel.get());
    delete attrs.id;
    attrs.title = `${attrs.title || 'Untitled'} (Copy)`;
    const duplicate = board.panelCollection.forge(attrs);
    board.panelCollection.add(duplicate);
  };

  handleEditPanel = async (panel) => {
    if (panel.get('type') === 'text') {
      const { onEditTextPanel } = this.props;
      if (onEditTextPanel) {
        onEditTextPanel(panel);
      }
      return;
    }

    const { board, history, location, $savedViews } = this.props;
    const config = panel.get('config');

    if (panel.get('type') === 'widget' && config.widgetName === 'dataExplorerQuery') {
      // dataExplorerQuery normally can't be edited, so create a saved view for the query and convert it to savedViewBrowser
      let { hash } = config;
      if (!hash) {
        hash = await getHashForQuery(config.query, { persist: true });
      }
      const view_name = panel.title || 'Untitled Query';
      const savedView = $savedViews.collection.forge({ view_name, saved_query_id: hash });
      await savedView.save({}, { toast: false });
      panel.set('config', {
        ...omit(config, ['hash', 'query']),
        widgetName: 'savedViewBrowser',
        savedViewId: savedView.id
      });
      await panel.save({}, { draft: true });
    }

    history.push(
      { pathname: `${this.basePath}/boards/${board.id}/panel/${panel.id}`, search: location.search },
      { from: location }
    );
  };

  handleLayoutChange = (layout) => {
    const { board } = this.props;
    board.layout = layout;
  };

  handleSectionChange = (panel, toSectionId) => {
    const currentSection = panel.get('parent_panel_id') ? panel.collection.get(panel.get('parent_panel_id')) : null;
    const targetSection = toSectionId ? panel.collection.get(toSectionId) : null;
    panel.safeSet({ parent_panel_id: toSectionId || null });

    if (currentSection) {
      this.handleSectionLayoutChange({ panel: currentSection });
    }
    if (targetSection) {
      this.handleSectionLayoutChange({ panel: targetSection });
    }

    return panel.save({}, { draft: true });
  };

  handleSectionLayoutChange = ({ panel, panelHeight = panel.getBodyContentsHeight() }) => {
    const { board } = this.props;
    const layout = panel.get('layout');

    // panelHeight is actually the body contents' height
    // this adds 38 for the header and 20 for the body's padding
    // if panelHeight is zero, we do not want to include body padding
    const heightPx = panelHeight === 0 ? 38 : 38 + GRID_CONTAINER_PADDING * 2 + panelHeight;

    // Formula for obtaining height in px from the `h` property is this:
    // const heightPx = h * GRID_ROW_HEIGHT + (h - 1) * GRID_MARGIN;
    // Reversed this actually looks like
    // const h = (heightPx + GRID_MARGIN) / (GRID_ROW_HEIGHT + GRID_MARGIN);
    const newLayout = structuredClone(layout);
    newLayout[board.breakpoint].h = (heightPx + GRID_MARGIN) / (GRID_ROW_HEIGHT + GRID_MARGIN);
    panel.set('layout', newLayout);
  };

  render() {
    const { loading, board, canEdit } = this.props;

    if (loading) {
      return (
        <Page>
          <Flex mt={2} alignItems="flex-start" justifyContent="center" width="100%">
            <Spinner />
          </Flex>
        </Page>
      );
    }

    if (!board) {
      return <NotFound />;
    }

    const panels = board.panelCollection.topLevelPanels;
    const { scrollRef, headerSlot, stickyToolbarSlot, onAddPanel } = this.props;

    return (
      <Flex flexDirection="column" bg="decks.background" flex={1} overflow="hidden scroll" zIndex={0} ref={scrollRef}>
        {headerSlot}
        {stickyToolbarSlot}
        {panels.length === 0 && (
          <EmptyState
            title="No panels"
            icon="new-grid-item"
            description={canEdit && `Start by adding something to this ${BOARD_LABEL.toLowerCase()}.`}
            action={canEdit && <AddPanelButton board={board} onAddPanel={onAddPanel} />}
          />
        )}

        <Flex flex={1}>
          <ChartSyncProvider board={board}>
            <BoardGridLayout
              board={board}
              panels={panels}
              layout={board.layout}
              onLayoutChange={this.handleLayoutChange}
              onAddPanel={onAddPanel}
              onEditPanel={this.handleEditPanel}
              onRemovePanel={this.handleRemovePanel}
              onDuplicatePanel={this.handleDuplicatePanel}
              onSectionLayoutChange={this.handleSectionLayoutChange}
              onSectionChange={this.handleSectionChange}
              canEdit={canEdit}
            />
          </ChartSyncProvider>
        </Flex>
      </Flex>
    );
  }
}

export default BoardGridView;
